/**
 * Voice Live WebRTC provider.
 *
 * Implements {@link VoiceAgentProvider} for Voice Live over **WebRTC via the
 * Media Gateway** (the v1 transport), for **both model and agent modes**. The
 * adapter owns the `RTCPeerConnection`: it builds the SDP offer,
 * hands it to the customer broker via the `negotiate` hook, applies
 * the returned `sdpAnswer`, streams mic audio over RTP, applies the session
 * config via a `session.update` on the `voice-live-events` data channel, and
 * maps data-channel Realtime events onto normalized events.
 *
 * Automatic reconnect/session resume is tracked separately.
 */

import {
  createStreamLevelMeter,
  type ClientToolPolicy,
  type ClientToolRegistry,
  type ConnectOptions,
  type ProviderConfig,
  type SessionGrant,
  type StreamLevelMeter,
  type VoiceAgentProvider,
  type VoiceAgentProviderCapabilities,
  type VoiceAgentProviderEvents,
  type SessionGrantCommon,
} from "@microsoft/voice-widget-core-client";

import { parseVoiceLiveConfig, type VoiceLiveConfig } from "./config.js";
import { mapRealtimeEvent, type RealtimeServerEvent } from "./realtime-events.js";
import { buildSessionUpdate } from "./session-config.js";
import { parseFunctionCalls, runClientTool } from "./client-tools.js";
import { wrapStatic } from "./client-tools-registry.js";

/**
 * The grant this adapter reads (v1): WebRTC via the Media Gateway. The broker
 * already did the SDP exchange (holding the credential + control WS); the
 * browser applies the answer and streams RTP directly. No client credential.
 * `sdpAnswer` is optional when the answer arrives via `ConnectOptions.negotiate`
 * after the provider creates its local offer.
 */
export type VoiceLiveWebRtcDirectGrant = SessionGrantCommon & {
  sdpAnswer?: string;
};

/** Data channel the Media Gateway uses for non-audio Realtime events. */
const EVENTS_CHANNEL = "voice-live-events";

/**
 * How long a `disconnected` peer connection may try to recover before the
 * session is treated as lost.
 *
 * Long enough to ride out an ICE restart or a network handover (which usually
 * settle in 1–3s), short enough that a user staring at a dead call is not left
 * guessing. On expiry the provider tears down exactly as before — including the
 * broker `endSession` call, which matters because the service does not reap a
 * session on its own when browser media stops.
 */
const RECONNECT_GRACE_MS = 8000;

/** Name this adapter registers under (matches the `provider` attribute). */
export const PROVIDER_NAME = "voicelive";

/** Wait until ICE gathering completes (non-trickle) so the offer carries candidates. */
async function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 2000): Promise<void> {
  if (pc.iceGatheringState === "complete") return;
  await new Promise<void>((resolve) => {
    const finish = (): void => {
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", check);
      resolve();
    };
    const check = (): void => {
      if (pc.iceGatheringState === "complete") finish();
    };
    const timer = setTimeout(finish, timeoutMs);
    pc.addEventListener("icegatheringstatechange", check);
  });
}

export class VoiceLiveWebRtcProvider implements VoiceAgentProvider<VoiceLiveConfig> {
  readonly name = PROVIDER_NAME;

  readonly capabilities: VoiceAgentProviderCapabilities = {
    supportsText: true,
    features: { modelMode: true, agentMode: true, clientTools: true, dynamicClientTools: true },
  };

  private pc: RTCPeerConnection | undefined;
  private channel: RTCDataChannel | undefined;
  private micStream: MediaStream | undefined;
  private audioEl: HTMLAudioElement | undefined;
  private levelMeter: StreamLevelMeter | undefined;
  /** Resolved config for the active session; used to build `session.update`. */
  private config: VoiceLiveConfig | undefined;
  /** Session id reported on connect: the broker-negotiated value (if any) wins over the grant's. */
  private sessionId: string | undefined;
  /** Host-registered client tools for this session. */
  private registry: ClientToolRegistry | undefined;
  /** Per-tool follow-up policy from the broker (grant → negotiate-result if present). */
  private clientToolPolicy: Record<string, ClientToolPolicy> = {};
  /** call_ids already dispatched, so each tool call runs at most once. */
  private handledCallIds = new Set<string>();
  /** Bumped on connect()/disconnect(); a tool captures it and becomes a no-op if it changes. */
  private connGen = 0;
  /** True while the gateway has an active response (between response.created/.done). */
  private responseActive = false;
  /** A `response.create` deferred because a response was active / tools still running. */
  private pendingResponseCreate = false;
  /** True once a consumer onRawEvent handler threw this session, so we report it only once. */
  private rawEventObserverThrew = false;
  /** Count of client tools currently executing, so the follow-up create waits for all. */
  private inFlightTools = 0;
  /** Broker-backed control event sender for media-only WebRTC browser sessions. */
  private sendSessionEvent: ConnectOptions["sendSessionEvent"];
  /** Broker session closer paired with the active negotiated session id. */
  private endSession: ConnectOptions["endSession"];
  /** A broker-routed typed turn waiting for the current response to finish. */
  private pendingControlResponseCreate = false;
  /** True once a terminal state has emitted `onDisconnect`, so the provider emits it at most once. */
  private terminated = false;
  /** True after this session has emitted its initial connected callbacks. */
  private hasConnected = false;
  /**
   * Pending grace timer for a `disconnected` peer connection.
   *
   * `disconnected` is NOT terminal: ICE keeps trying and the connection often
   * returns to `connected` within a few seconds (a WiFi/cellular handover, a
   * brief tunnel, a phone waking from lock). Tearing down on it stops the mic
   * track and ends the broker session, which destroys the very state the
   * recovery would have reused — so the drop becomes permanent because we
   * reacted to it, not because the network failed.
   *
   * The service's own reference sample takes the same position: it acts on
   * `failed` only and lets `disconnected` ride. See the Voice Live WebRTC docs.
   */
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  parseConfig(raw: ProviderConfig): VoiceLiveConfig {
    return parseVoiceLiveConfig(raw);
  }

  async connect(
    grant: SessionGrant,
    config: VoiceLiveConfig,
    ev: VoiceAgentProviderEvents,
    opts?: ConnectOptions,
  ): Promise<void> {
    // The core `SessionGrant` is provider-neutral; narrow it to the shape this
    // adapter reads (the v1 WebRTC/direct grant).
    const webrtcGrant = grant as VoiceLiveWebRtcDirectGrant;

    this.connGen++;
    ev.onStatusChange?.("connecting");
    this.config = config;
    this.sessionId = grant.sessionId;
    this.registry = opts?.clientToolRegistry ?? wrapStatic(opts?.clientTools);
    this.sendSessionEvent = opts?.sendSessionEvent;
    this.endSession = opts?.endSession;
    this.handledCallIds.clear();
    this.responseActive = false;
    this.pendingResponseCreate = false;
    this.pendingControlResponseCreate = false;
    this.inFlightTools = 0;
    this.rawEventObserverThrew = false;
    this.terminated = false;
    this.hasConnected = false;
    try {
      // 1. Mic capture — the raw track is added to the peer connection (RTP).
      //    Honor the config's audio-processing knobs (default on).
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: config.echoCancellation ?? true,
          noiseSuppression: config.noiseSuppression ?? true,
          autoGainControl: true,
        },
      });

      // 2. Peer connection + control data channel (offerer creates the channel).
      const pc = new RTCPeerConnection();
      this.pc = pc;
      this.channel = pc.createDataChannel(EVENTS_CHANNEL);
      this.wireDataChannel(this.channel, ev);
      for (const track of this.micStream.getAudioTracks()) {
        pc.addTrack(track, this.micStream);
      }

      // 3. Remote audio → <audio> playback + level meter for the visualizer.
      pc.ontrack = (e: RTCTrackEvent): void => this.attachRemoteAudio(e.streams[0]);
      pc.onconnectionstatechange = (): void => this.onConnectionStateChange(pc, grant, ev);

      // 4. SDP offer/answer: offer → broker (negotiate) → sdpAnswer.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);

      const localSdp = pc.localDescription?.sdp ?? offer.sdp ?? "";
      const answerSdp = await this.resolveAnswer(localSdp, webrtcGrant, opts);
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
      // Connection completion + onConnect fire from onconnectionstatechange.
    } catch (err) {
      ev.onError?.({
        code: "connect_failed",
        message: err instanceof Error ? err.message : String(err),
        recoverable: false,
      });
      await this.disconnect().catch(() => {});
      ev.onStatusChange?.("error");
      throw err;
    }
  }

  /** Get the SDP answer: prefer the live broker exchange, else a pre-negotiated grant. */
  private async resolveAnswer(
    offerSdp: string,
    grant: VoiceLiveWebRtcDirectGrant,
    opts: ConnectOptions | undefined,
  ): Promise<string> {
    // Grant-init: always start from the grant's policy (or empty). A negotiate result may replace it.
    this.clientToolPolicy = grant.clientToolPolicy ?? {};
    if (opts?.negotiate) {
      const result = await opts.negotiate(offerSdp);
      if (typeof result === "string") return result;
      // Honor the optional session metadata the broker may return alongside the answer.
      if (result.sessionId) this.sessionId = result.sessionId;
      // Negotiate result carries a policy -> it REPLACES the grant policy.
      // An explicit {} clears; omitted keeps the grant's.
      if (result.clientToolPolicy !== undefined) this.clientToolPolicy = result.clientToolPolicy;
      return result.sdpAnswer;
    }
    if (grant.sdpAnswer) return grant.sdpAnswer;
    throw new Error(
      "provider-voicelive: no SDP answer — supply ConnectOptions.negotiate (broker exchange) " +
        "or a pre-negotiated SessionGrant.sdpAnswer.",
    );
  }

  private wireDataChannel(channel: RTCDataChannel, ev: VoiceAgentProviderEvents): void {
    // On open, push the initial session config so voice/turn-detection/etc. apply.
    channel.onopen = (): void => {
      this.sendSessionUpdate();
    };
    channel.onmessage = (e: MessageEvent): void => {
      if (typeof e.data !== "string") return;
      let event: RealtimeServerEvent;
      try {
        event = JSON.parse(e.data) as RealtimeServerEvent;
      } catch {
        return;
      }
      // Fire the raw observer FIRST so a debugging consumer sees every event even when a
      // later stage throws — but isolate it: it is consumer code on the hot path of every
      // message, and a throw here would skip mapping, response-state tracking and tool
      // dispatch for this event. (Same rule as the onUnhandledClientToolCall notification.)
      try {
        ev.onRawEvent?.(event);
      } catch (rawEventError) {
        // Report the first throw per session and stay quiet afterwards: this runs for EVERY
        // data-channel message, so logging each one would flood the console on a hot path —
        // but staying fully silent leaves the consumer with a broken observer and no signal.
        if (!this.rawEventObserverThrew) {
          this.rawEventObserverThrew = true;
          console.error(
            "[voice-agent] onRawEvent handler threw; it is isolated from event processing. " +
              "Further throws from it are not logged for this session.",
            rawEventError,
          );
        }
      }
      mapRealtimeEvent(event, ev);
      // For a `response.done`, dispatch any tool calls it carries BEFORE flushing a
      // deferred `response.create`: a call that appears only in `response.done` (gateway
      // fallback path) must have its output submitted before the continuation is sent,
      // else its result is stranded for the next turn. Sync tools then defer their own
      // create (responseActive still true here) and it flushes as one. For every other
      // event type the order is track-then-handle.
      if (event.type === "response.done") {
        this.handleToolCall(event, ev);
        this.trackResponseState(event);
      } else {
        this.trackResponseState(event);
        this.handleToolCall(event, ev);
      }
    };
  }

  /**
   * Track whether a response is active so a tool follow-up `response.create` isn't sent
   * mid-response (the gateway rejects that with `conversation_already_has_active_response`,
   * which silently drops the agent's spoken tool confirmation). When the active response
   * ends, try to flush a deferred `response.create`.
   */
  private trackResponseState(event: RealtimeServerEvent): void {
    if (event.type === "response.created") {
      this.responseActive = true;
    } else if (event.type === "response.done") {
      this.responseActive = false;
      this.flushPendingControlResponseCreate();
      this.flushPendingResponseCreate();
    } else if (event.type === "error") {
      this.reconcileOnError(event);
    }
  }

  private flushPendingControlResponseCreate(): void {
    // Mirror flushPendingResponseCreate: a create must not go out while a response is
    // active nor while tool outputs are still pending (else a response.done batch strands
    // the later results). Both conditions must clear first.
    if (!this.pendingControlResponseCreate || this.responseActive || this.inFlightTools > 0) {
      return;
    }
    const sender = this.sendSessionEvent;
    const sessionId = this.sessionId;
    if (!sender || !sessionId) return;
    this.pendingControlResponseCreate = false;
    this.responseActive = true;
    void sender(sessionId, { type: "response.create" }).catch(() => {
      // Roll back both flags so the drain path can retry, matching flushPendingResponseCreate.
      this.responseActive = false;
      this.pendingControlResponseCreate = true;
    });
  }

  /**
   * Reconcile the optimistic `responseActive` flag against a gateway `error`.
   *
   * {@link flushPendingResponseCreate} sets the flag before the gateway echoes
   * `response.created`. If the gateway answers with an `error` instead, no
   * `response.created`/`response.done` pair ever arrives — so without this the flag
   * latches `true` for the rest of the session and every later tool follow-up is
   * silently swallowed.
   */
  private reconcileOnError(event: RealtimeServerEvent): void {
    const err = (event["error"] as Record<string, unknown> | undefined) ?? {};
    if (err["code"] === "conversation_already_has_active_response") {
      // The gateway confirms a response IS active and ours was dropped: the flag is
      // accurate, so keep it and re-arm the request instead of losing the tool
      // confirmation. It flushes on that response's `response.done`.
      this.responseActive = true;
      this.pendingResponseCreate = true;
      return;
    }
    // Any other error may be the rejection of our optimistic create. Clearing is the safe
    // direction: a stuck `true` mutes every later follow-up for the session, while a stuck
    // `false` costs at most one rejected create — which comes back as the code above and
    // re-arms itself.
    this.responseActive = false;
    this.flushPendingResponseCreate();
  }

  /**
   * Request a single follow-up `response.create` after tool output(s), so the agent speaks
   * its confirmation. It must not be sent while a response is active (the gateway rejects
   * that as `conversation_already_has_active_response`) nor while other tools are still
   * running (their outputs must all be submitted first, else a parallel/`response.done`
   * batch would strand the later results). So we only mark that a create is wanted and let
   * {@link flushPendingResponseCreate} send exactly one once both conditions clear.
   */
  private requestResponseCreate(): void {
    this.pendingResponseCreate = true;
    this.flushPendingResponseCreate();
  }

  /** Send the single deferred `response.create` iff no response is active and no tool is running. */
  private flushPendingResponseCreate(): void {
    if (!this.pendingResponseCreate || this.responseActive || this.inFlightTools > 0) return;
    // Mark active optimistically (before the gateway's own `response.created` echo) so a
    // later event can't slip a second create in before we hear back.
    this.pendingResponseCreate = false;
    this.responseActive = true;
    if (this.sendClientEvent({ type: "response.create" })) return;
    // The send failed (channel closed, or `send()` threw). Roll BOTH flags back: keeping
    // the optimistic flag would mute every later follow-up, and dropping the request would
    // lose this tool's spoken confirmation. Re-armed, it retries on the next flush trigger.
    this.responseActive = false;
    this.pendingResponseCreate = true;
  }

  /** Execute host client tool(s) for any not-yet-handled function calls in this event. */
  private handleToolCall(event: RealtimeServerEvent, ev: VoiceAgentProviderEvents): void {
    for (const call of parseFunctionCalls(event)) {
      if (this.handledCallIds.has(call.callId)) continue;
      this.handledCallIds.add(call.callId);
      // Capture the generation + count BEFORE any host callback: a notification may call
      // stop()/disconnect() synchronously (bumping connGen), and the stale tool must then
      // no-op instead of emitting onto or mutating the superseded/next session.
      const genAtStart = this.connGen;
      this.inFlightTools++;
      const handler = this.registry?.get(call.name);
      const followUpResponse = this.clientToolPolicy[call.name]?.followUpResponse !== false;
      if (!handler) {
        try {
          ev.onUnhandledClientToolCall?.({ name: call.name, args: call.args, callId: call.callId });
        } catch {
          /* a throwing notification must not block the standard error output */
        }
      }
      void runClientTool(call, handler, followUpResponse, (e) => {
        // Report delivery back to runClientTool: a dropped output must not be followed by
        // a `response.create` (the model would answer with no tool result). A stale
        // generation counts as undelivered, which also stops the continuation.
        if (this.connGen !== genAtStart) return false;
        if (this.isResponseCreate(e)) {
          this.requestResponseCreate();
          return true;
        }
        return this.sendClientEvent(e);
      })
        .catch(() => {
          // A data-channel send race (e.g. InvalidStateError) must not surface as an
          // unhandled rejection; sendClientEvent's readyState guard covers the rest.
        })
        .finally(() => {
          if (this.connGen !== genAtStart) return;
          this.inFlightTools = Math.max(0, this.inFlightTools - 1);
          this.flushPendingControlResponseCreate();
          this.flushPendingResponseCreate();
        });
    }
  }

  private isResponseCreate(e: unknown): boolean {
    return !!e && typeof e === "object" && (e as { type?: unknown }).type === "response.create";
  }

  /** Send a raw Realtime client event over the data channel if it is open. */
  private sendClientEvent(event: unknown): boolean {
    const channel = this.channel;
    if (!channel || channel.readyState !== "open") return false;
    try {
      channel.send(JSON.stringify(event));
      return true;
    } catch {
      // `send()` can throw despite an "open" readyState (a closing race, or a full send
      // buffer). Report it so callers roll back optimistic state, instead of letting the
      // throw escape a DOM event handler or become an unhandled rejection from the tool
      // `.finally()` (which runs after that chain's `.catch()`).
      return false;
    }
  }

  /**
   * Apply session config via a `session.update`. Uses the active
   * config by default; pass an override to reconfigure mid-session. Returns
   * `false` if the data channel is not open yet (the initial update is sent
   * automatically on open).
   */
  sendSessionUpdate(config?: VoiceLiveConfig): boolean {
    const target = config ?? this.config;
    if (!target) return false;
    return this.sendClientEvent(buildSessionUpdate(target));
  }

  async sendText(text: string): Promise<void> {
    const normalized = text.trim();
    if (!normalized) throw new Error("provider-voicelive: text must not be blank.");
    const message = {
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text: normalized }],
      },
    };
    const sender = this.sendSessionEvent;
    const sessionAtStart = this.sessionId;
    if (sender && sessionAtStart) {
      await sender(sessionAtStart, message);
      // A disconnect + reconnect during the round trip would make `this.sessionId` a
      // different session; a response.create (or deferred arm) aimed at it would strand a
      // create with no matching conversation.item.create. Bail if identity moved on.
      if (this.sessionId !== sessionAtStart) return;
      if (this.responseActive) {
        this.pendingControlResponseCreate = true;
        return;
      }
      this.responseActive = true;
      try {
        await sender(sessionAtStart, { type: "response.create" });
      } catch (error) {
        this.responseActive = false;
        throw error;
      }
      return;
    }
    if (!this.sendClientEvent(message)) {
      throw new Error("provider-voicelive: cannot send text before the data channel is open.");
    }
    this.requestResponseCreate();
  }

  private attachRemoteAudio(stream: MediaStream | undefined): void {
    if (!stream) return;
    const audio = new Audio();
    audio.autoplay = true;
    audio.srcObject = stream;
    void audio.play().catch(() => {
      /* autoplay may require a gesture; the trigger click already provided one */
    });
    this.audioEl = audio;
    this.levelMeter = createStreamLevelMeter(stream);
  }

  private onConnectionStateChange(
    pc: RTCPeerConnection,
    grant: SessionGrant,
    ev: VoiceAgentProviderEvents,
  ): void {
    switch (pc.connectionState) {
      case "connected":
        // Recovery inside the grace window is transparent to the host: core state
        // never left "connected", so a second onConnect would look like a new call.
        this.clearReconnectTimer();
        if (this.hasConnected) break;
        this.hasConnected = true;
        ev.onStatusChange?.("connected");
        ev.onConnect?.({ sessionId: this.sessionId ?? grant.sessionId, transport: "webrtc" });
        ev.onModeChange?.("listening");
        break;
      case "disconnected": {
        // Recoverable, not terminal — give ICE a chance before tearing down.
        // Nothing is emitted yet: reporting a disconnect here would show the
        // user a dead call that is about to come back on its own.
        if (this.terminated || this.reconnectTimer) break;
        const gen = this.connGen;
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = undefined;
          // A disconnect()/connect() in the meantime owns the lifecycle now.
          if (this.terminated || gen !== this.connGen) return;
          this.emitTerminal(ev, "disconnected", "disconnected");
        }, RECONNECT_GRACE_MS);
        break;
      }
      case "closed":
        // Terminal by definition: the peer connection cannot leave this state,
        // so there is nothing to wait for.
        if (this.terminated) break;
        this.emitTerminal(ev, "disconnected", "closed");
        break;
      case "failed":
        // ICE gave up. The reference sample tears down here too.
        if (this.terminated) break;
        this.emitTerminal(ev, "error", "failed");
        break;
      default:
        break;
    }
  }

  /** Cancel a pending grace-period teardown, if one is armed. */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer === undefined) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
  }

  /**
   * Report a session as over and release everything. Shared by the terminal
   * connection states so the status/reason pairing and the teardown call stay
   * in one place.
   */
  private emitTerminal(
    ev: VoiceAgentProviderEvents,
    status: "disconnected" | "error",
    reason: string,
  ): void {
    ev.onStatusChange?.(status);
    ev.onDisconnect?.({ reason });
    // Releases the peer connection, mic, and — importantly — the broker session:
    // the service keeps a session alive until told otherwise, so skipping this
    // would strand a billable session server-side.
    void this.disconnect().catch(() => {
      /* no-op */
    });
  }

  setMuted(muted: boolean): void {
    for (const track of this.micStream?.getAudioTracks() ?? []) {
      track.enabled = !muted;
    }
  }

  getOutputLevel(): number {
    return this.levelMeter?.getLevel() ?? 0;
  }

  async disconnect(): Promise<void> {
    this.terminated = true;
    this.connGen++;
    // Drop any armed grace timer: without this a pending teardown could fire
    // after a later connect() and report a disconnect for the new session.
    this.clearReconnectTimer();
    const sessionId = this.sessionId;
    const endSession = this.endSession;
    this.sessionId = undefined;
    this.endSession = undefined;
    this.levelMeter?.stop();
    this.levelMeter = undefined;

    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl = undefined;
    }
    if (this.channel) {
      this.channel.onopen = null;
      this.channel.onmessage = null;
      this.channel.close();
      this.channel = undefined;
    }
    for (const track of this.micStream?.getTracks() ?? []) track.stop();
    this.micStream = undefined;
    this.config = undefined;
    this.registry = undefined;
    this.clientToolPolicy = {};
    this.sendSessionEvent = undefined;
    this.handledCallIds.clear();
    this.responseActive = false;
    this.pendingResponseCreate = false;
    this.pendingControlResponseCreate = false;
    this.inFlightTools = 0;
    this.rawEventObserverThrew = false;
    this.hasConnected = false;

    if (this.pc) {
      this.pc.ontrack = null;
      this.pc.onconnectionstatechange = null;
      this.pc.close();
      this.pc = undefined;
    }

    if (sessionId && endSession) {
      void endSession(sessionId).catch(() => {
        // Local media teardown is authoritative. A broker that cannot process
        // the best-effort end request must not strand the browser in teardown.
      });
    }
  }
}
